import React, { useCallback, useEffect, useMemo, useState } from "react";

import { CButton, CCard, CCardBody, CSpinner } from "@coreui/react";
import { FaTrash, FaUsers } from "react-icons/fa";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";

import Breadcrumb from "../../../components/Breadcrumb";
import FiltroMultiplo from "../../../components/Formulario/FiltroMultiplo";
import Loading from "../../../components/Loading";
import ProtectedRoute from "../../../components/ProtectedRoute";
import URL from "../../../config/api";
import axios from "../../../config/axios";
import "./styles.css";

/* Agendamento de formulário por público.

   Os filtros viram os critérios do agendamento, e não uma lista de pessoas: o
   backend (PublicoAlvoService) calcula quem eles alcançam a cada consulta, e
   quem entra depois na turma, na escola ou no grupo recebe o formulário
   enquanto o agendamento vale.

   Cascata: Regional e Escola carregam a partir do Grupo; Série, a partir do
   Grupo (sem grupo, só para quem alcança a plataforma toda); Turma, depois de
   Escola ou Série, porque sem nenhum dos dois seriam milhares. Vazio é
   "Todos" dentro do nível de cima. Quem tem grupo, regional ou escola fixos
   no papel já abre com eles marcados e travados. */

// Cabeçalho de cada critério na tabela de agendamentos, e o texto de "nenhum
// filtro escolhido" naquele nível.
const COLUNAS_DE_CRITERIO = [
  ["perfis", "Perfis"],
  ["grupos", "Grupos"],
  ["regionais", "Regionais"],
  ["escolas", "Escolas"],
  ["series", "Séries"],
  ["turmas", "Turmas"]
];
const TODOS_DO_CAMPO = {
  perfis: "—",
  grupos: "Todos",
  regionais: "Todas",
  escolas: "Todas",
  series: "Todas",
  turmas: "Todas"
};
const PERFIL_ESTUDANTE = 7;

const auth = token => ({ headers: { authorization: `Bearer ${token}` } });

const agoraLocal = () => {
  const data = new Date();
  data.setSeconds(0, 0);
  const deslocamento = data.getTimezoneOffset() * 60000;
  return new Date(data - deslocamento).toISOString().slice(0, 16);
};

const formatarDataHora = valor => {
  if (!valor) return "—";
  const data = new Date(String(valor).replace(" ", "T"));
  if (Number.isNaN(data.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(data);
};

const numero = valor => new Intl.NumberFormat("pt-BR").format(valor || 0);

/* Guarda só o que ainda existe nas opções recarregadas: trocar o grupo não
   pode deixar marcada uma regional de outro grupo. */
const podar = (selecionados, opcoes) =>
  selecionados.filter(id => opcoes.some(opcao => opcao.id === id));

function AgendamentoFormulario({ user, general, match }) {
  const token = user.token;
  const formularioId = match.params.id;
  const papel = general.active_roles || {};
  const fixos = useMemo(
    () => ({
      grupos: papel.group_id ? [Number(papel.group_id)] : [],
      regionais: papel.region_id ? [Number(papel.region_id)] : [],
      escolas: papel.school_id ? [Number(papel.school_id)] : []
    }),
    [papel.group_id, papel.region_id, papel.school_id]
  );

  const [formulario, setFormulario] = useState(null);
  const [carregandoPagina, setCarregandoPagina] = useState(true);
  const [opcoes, setOpcoes] = useState({
    perfis: [],
    grupos: [],
    regionais: [],
    escolas: [],
    series: [],
    turmas: []
  });
  const [carregando, setCarregando] = useState({});
  const [criterios, setCriterios] = useState(() => ({
    perfis: [PERFIL_ESTUDANTE],
    grupos: fixos.grupos,
    regionais: fixos.regionais,
    escolas: fixos.escolas,
    series: [],
    turmas: []
  }));
  const [inicio, setInicio] = useState(agoraLocal);
  const [fim, setFim] = useState("");
  const [pessoas, setPessoas] = useState(null);
  const [contando, setContando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [removendo, setRemovendo] = useState(null);

  const buscarOpcoes = useCallback(
    async (campo, params = {}) => {
      setCarregando(old => ({ ...old, [campo]: true }));
      try {
        const query = {};
        Object.entries(params).forEach(([chave, valor]) => {
          query[chave] = JSON.stringify(valor);
        });
        const resposta = await axios.get(
          `${URL.api}formulario/publico/${campo}`,
          { ...auth(token), params: query }
        );
        const lista = (resposta.data?.data || []).map(item => ({
          ...item,
          detalhe: campo === "turmas" ? item.school_name : undefined
        }));
        setOpcoes(old => ({ ...old, [campo]: lista }));
        return lista;
      } catch (error) {
        toastr.error(
          "Erro",
          error.response?.data?.error || "Não foi possível carregar os filtros."
        );
        setOpcoes(old => ({ ...old, [campo]: [] }));
        return [];
      } finally {
        setCarregando(old => ({ ...old, [campo]: false }));
      }
    },
    [token]
  );

  const carregarAgendamentos = useCallback(() => {
    axios
      .get(`${URL.api}formulario/${formularioId}/allocations`, auth(token))
      .then(resposta => setAgendamentos(resposta.data?.data || []))
      .catch(() => setAgendamentos([]));
  }, [formularioId, token]);

  // Abertura: o formulário, os perfis, os grupos e o que já foi agendado
  useEffect(() => {
    let ativo = true;
    Promise.all([
      axios.get(`${URL.api}formulario/${formularioId}`, auth(token)),
      buscarOpcoes("perfis"),
      buscarOpcoes("grupos")
    ])
      .then(([resposta]) => ativo && setFormulario(resposta.data?.data || null))
      .catch(error =>
        toastr.error(
          "Erro",
          error.response?.data?.error || "Não foi possível abrir o formulário."
        )
      )
      .finally(() => ativo && setCarregandoPagina(false));
    carregarAgendamentos();
    return () => {
      ativo = false;
    };
  }, [formularioId, token, buscarOpcoes, carregarAgendamentos]);

  const { grupos, regionais, escolas, series } = criterios;
  const chaveGrupos = grupos.join(",");
  const chaveRegionais = regionais.join(",");
  const chaveEscolas = escolas.join(",");
  const chaveSeries = series.join(",");

  const aposCarregar = (campo, lista) =>
    setCriterios(old => ({ ...old, [campo]: podar(old[campo], lista) }));

  // Regional: depende do grupo
  useEffect(() => {
    if (!grupos.length) {
      setOpcoes(old => ({ ...old, regionais: [] }));
      if (!fixos.regionais.length)
        setCriterios(old => ({ ...old, regionais: [] }));
      return;
    }
    buscarOpcoes("regionais", { grupos }).then(lista =>
      aposCarregar("regionais", lista)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveGrupos, buscarOpcoes]);

  // Escola: depende do grupo, e respeita as regionais escolhidas
  useEffect(() => {
    if (!grupos.length) {
      setOpcoes(old => ({ ...old, escolas: [] }));
      if (!fixos.escolas.length) setCriterios(old => ({ ...old, escolas: [] }));
      return;
    }
    buscarOpcoes("escolas", { grupos, regionais }).then(lista =>
      aposCarregar("escolas", lista)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveGrupos, chaveRegionais, buscarOpcoes]);

  // Série: as que existem nas turmas ativas do recorte
  useEffect(() => {
    buscarOpcoes("series", { grupos, regionais, escolas }).then(lista =>
      aposCarregar("series", lista)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveGrupos, chaveRegionais, chaveEscolas, buscarOpcoes]);

  // Turma: só com grupo e com escola ou série escolhidas
  const turmaLiberada =
    grupos.length > 0 && (escolas.length > 0 || series.length > 0);
  useEffect(() => {
    if (!turmaLiberada) {
      setOpcoes(old => ({ ...old, turmas: [] }));
      setCriterios(old => ({ ...old, turmas: [] }));
      return;
    }
    buscarOpcoes("turmas", { grupos, regionais, escolas, series }).then(lista =>
      aposCarregar("turmas", lista)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chaveGrupos,
    chaveRegionais,
    chaveEscolas,
    chaveSeries,
    turmaLiberada,
    buscarOpcoes
  ]);

  // Quantas pessoas os filtros alcançam hoje (atualiza ao mexer, com espera)
  useEffect(() => {
    if (!criterios.perfis.length) {
      setPessoas(0);
      return undefined;
    }
    let ativo = true;
    setContando(true);
    const espera = window.setTimeout(() => {
      axios
        .post(
          `${URL.api}formulario/publico/contagem`,
          { ...criterios, inicio: inicio || undefined, fim: fim || undefined },
          auth(token)
        )
        .then(
          resposta => ativo && setPessoas(resposta.data?.data?.pessoas ?? 0)
        )
        .catch(() => ativo && setPessoas(null))
        .finally(() => ativo && setContando(false));
    }, 450);
    return () => {
      ativo = false;
      window.clearTimeout(espera);
    };
  }, [criterios, inicio, fim, token]);

  const mudar = campo => valores =>
    setCriterios(old => ({ ...old, [campo]: valores }));

  const agendar = async () => {
    if (!fim) {
      toastr.error(
        "Atenção",
        "Informe até quando o formulário fica disponível."
      );
      return;
    }
    if (new Date(fim) <= new Date(inicio)) {
      toastr.error("Atenção", "O fim precisa ser depois do início.");
      return;
    }
    setSalvando(true);
    try {
      const resposta = await axios.post(
        `${URL.api}formulario/${formularioId}/allocations`,
        {
          criterios,
          inicio: inicio.replace("T", " "),
          fim: fim.replace("T", " ")
        },
        auth(token)
      );
      toastr.success(
        "Agendado com sucesso!",
        `O formulário foi enviado para ${numero(
          resposta.data?.data?.pessoas
        )} pessoa(s).`
      );
      carregarAgendamentos();
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível agendar o formulário."
      );
    } finally {
      setSalvando(false);
    }
  };

  const remover = async agendamento => {
    if (
      !window.confirm(
        "Remover este agendamento? Quem ainda não respondeu deixa de receber o formulário."
      )
    )
      return;
    setRemovendo(agendamento.id);
    try {
      await axios.post(
        `${URL.api}formulario/allocations/delete`,
        { id: agendamento.id },
        auth(token)
      );
      setAgendamentos(old => old.filter(item => item.id !== agendamento.id));
      toastr.success("Sucesso", "Agendamento removido.");
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível remover o agendamento."
      );
    } finally {
      setRemovendo(null);
    }
  };

  /* Uma célula da tabela de agendamentos: os nomes que o backend devolve
     (`nomes`), um por linha; lista vazia é "Todos" naquele nível. O
     agendamento antigo, por turma, só tem a turma. */
  const celulaCriterio = (agendamento, campo) => {
    if (!agendamento.nomes) {
      if (campo === "turmas" && agendamento.turma) return agendamento.turma;
      return <span className="text-muted">—</span>;
    }
    const lista = agendamento.nomes[campo] || [];
    if (!lista.length) {
      return <span className="text-muted">{TODOS_DO_CAMPO[campo]}</span>;
    }
    return (
      <ul className="formulario-agendar__nomes">
        {lista.map(nome => (
          <li key={nome}>{nome}</li>
        ))}
      </ul>
    );
  };

  if (carregandoPagina) return <Loading top="45" left="50" />;

  const podeAgendar =
    criterios.perfis.length > 0 && inicio && fim && pessoas > 0 && !salvando;

  return (
    <div className="animated fadeIn formulario-agendar">
      <Breadcrumb
        currentName="Agendar formulário"
        items={[
          {
            path: "/formulario",
            to: "/formulario",
            name: "Listagem de Formulários"
          }
        ]}
      />

      <header className="formulario-agendar__header">
        <h1>
          Agendar <strong>{formulario?.titulo}</strong>
        </h1>
        <p>
          Escolha quem deve responder. Quem entrar depois no grupo, na escola ou
          na turma também recebe o formulário enquanto o agendamento valer.
        </p>
      </header>

      <CCard className="formulario-agendar__card">
        <CCardBody>
          <span className="formulario-agendar__secao">Filtros</span>
          <div className="formulario-agendar__filtros">
            <FiltroMultiplo
              rotulo="Grupo"
              opcoes={opcoes.grupos}
              selecionados={criterios.grupos}
              onChange={mudar("grupos")}
              carregando={carregando.grupos}
              disabled={fixos.grupos.length > 0}
            />
            <FiltroMultiplo
              rotulo="Regional"
              opcoes={opcoes.regionais}
              selecionados={criterios.regionais}
              onChange={mudar("regionais")}
              carregando={carregando.regionais}
              disabled={!criterios.grupos.length || fixos.regionais.length > 0}
              dica="Escolha o grupo primeiro"
            />
            <FiltroMultiplo
              rotulo="Escola"
              opcoes={opcoes.escolas}
              selecionados={criterios.escolas}
              onChange={mudar("escolas")}
              carregando={carregando.escolas}
              disabled={!criterios.grupos.length || fixos.escolas.length > 0}
              dica="Escolha o grupo primeiro"
            />
            <FiltroMultiplo
              rotulo="Série"
              opcoes={opcoes.series}
              selecionados={criterios.series}
              onChange={mudar("series")}
              carregando={carregando.series}
            />
            <FiltroMultiplo
              rotulo="Turma"
              opcoes={opcoes.turmas}
              selecionados={criterios.turmas}
              onChange={mudar("turmas")}
              carregando={carregando.turmas}
              disabled={!turmaLiberada}
              dica="Escolha a escola ou a série primeiro"
            />
            <FiltroMultiplo
              rotulo="Perfil"
              opcoes={opcoes.perfis}
              selecionados={criterios.perfis}
              onChange={mudar("perfis")}
              carregando={carregando.perfis}
              semTodos
            />
          </div>
          <p className="formulario-agendar__nota">
            Série e turma valem para estudantes e professores. Os perfis de
            administração entram pelo grupo, pela regional e pela escola.
          </p>
        </CCardBody>
      </CCard>

      <CCard className="formulario-agendar__card">
        <CCardBody className="formulario-agendar__resumo">
          <div className="formulario-agendar__publico" aria-live="polite">
            <span className="formulario-agendar__publico-icone">
              <FaUsers aria-hidden="true" />
            </span>
            <div>
              <strong>
                {contando ? (
                  <CSpinner size="sm" />
                ) : pessoas === null ? (
                  "—"
                ) : (
                  `${numero(pessoas)} pessoa(s)`
                )}
              </strong>
              <small>alcançadas hoje por estes filtros</small>
            </div>
          </div>
          <label className="formulario-agendar__data">
            <span>Início</span>
            <input
              type="datetime-local"
              className="form-control"
              value={inicio}
              onChange={evento => setInicio(evento.target.value)}
            />
          </label>
          <label className="formulario-agendar__data">
            <span>Fim</span>
            <input
              type="datetime-local"
              className="form-control"
              value={fim}
              min={inicio}
              onChange={evento => setFim(evento.target.value)}
            />
          </label>
          <CButton color="primary" disabled={!podeAgendar} onClick={agendar}>
            {salvando ? <CSpinner size="sm" /> : "Agendar"}
          </CButton>
        </CCardBody>
      </CCard>

      <section className="formulario-agendar__lista">
        <h2>Agendamentos deste formulário</h2>
        {agendamentos.length ? (
          <div className="formulario-agendar__tabela table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  {COLUNAS_DE_CRITERIO.map(([campo, titulo]) => (
                    <th key={campo} scope="col">
                      {titulo}
                    </th>
                  ))}
                  <th scope="col">Período</th>
                  <th scope="col" className="text-right">
                    Público hoje
                  </th>
                  <th scope="col" className="text-right">
                    Responderam
                  </th>
                  <th scope="col">Agendado por</th>
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {agendamentos.map(agendamento => (
                  <tr key={agendamento.id}>
                    {COLUNAS_DE_CRITERIO.map(([campo]) => (
                      <td key={campo}>{celulaCriterio(agendamento, campo)}</td>
                    ))}
                    <td className="formulario-agendar__periodo">
                      {formatarDataHora(agendamento.start)}
                      <br />
                      até {formatarDataHora(agendamento.end)}
                    </td>
                    <td className="text-right">
                      {agendamento.pessoas === null ||
                      agendamento.pessoas === undefined
                        ? "—"
                        : numero(agendamento.pessoas)}
                    </td>
                    <td className="text-right">
                      {agendamento.responderam === null ||
                      agendamento.responderam === undefined
                        ? "—"
                        : numero(agendamento.responderam)}
                    </td>
                    <td>
                      {agendamento.agendado_por || "—"}
                      {agendamento.agendado_em && (
                        <small className="d-block text-muted">
                          {formatarDataHora(agendamento.agendado_em)}
                        </small>
                      )}
                    </td>
                    <td className="text-right">
                      <CButton
                        color="danger"
                        variant="ghost"
                        title="Remover agendamento"
                        aria-label="Remover agendamento"
                        disabled={removendo === agendamento.id}
                        onClick={() => remover(agendamento)}
                      >
                        {removendo === agendamento.id ? (
                          <CSpinner size="sm" />
                        ) : (
                          <FaTrash />
                        )}
                      </CButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">Este formulário ainda não foi agendado.</p>
        )}
      </section>
    </div>
  );
}

// Quem abre a tela é o access_roles_links do papel (link /formulario/allocate),
// conferido pelo ProtectedRoute. Fica fora do AgendamentoFormulario porque ele
// tem dois returns (carregando e a tela) e a checagem vale para os dois.
function FormularioAllocate(props) {
  const { currentRoute } = props;
  return (
    <>
      <ProtectedRoute routeId={currentRoute} />
      <AgendamentoFormulario {...props} />
    </>
  );
}

export default connect(state => ({
  user: state.user,
  general: state.general
}))(FormularioAllocate);
